import { useEffect, useState } from 'react'
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react'
import { parseTimeInput } from '@/utils/time'

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' | 'ghost' }) {
  const styles: Record<string, string> = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700',
    secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    ghost: 'text-slate-600 hover:bg-slate-100',
  }
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition disabled:opacity-50 ${styles[variant]} ${className}`}
      {...props}
    />
  )
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 ${className}`}
      {...props}
    />
  )
}

export function Select({ className = '', children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={`w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 ${className}`}
      {...props}
    >
      {children}
    </select>
  )
}

/**
 * Čas jako text, ne `<input type="time">` — ten na desktopu nutí psát přes
 * dvojtečku a v každém prohlížeči vypadá jinak. Tady se píše volně ("850")
 * a při opuštění pole se zápis srovná na "HH:mm" (viz parseTimeInput).
 *
 * `onChange` dostane kanonický čas, nebo prázdný řetězec, když je vstup
 * nesmyslný — rodič tak pozná, že nemá co ukládat.
 */
export function TimeInput({
  value,
  onChange,
  className = '',
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string
  onChange: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)

  // Přepíšeme rozepsaný text jen tehdy, když rodič opravdu drží jiný čas.
  // Bez té podmínky by po odmítnutém vstupu (onChange('')) effect smazal
  // uživateli z pole to, co právě napsal — a on by neviděl, co je špatně.
  useEffect(() => {
    if (value && value !== parseTimeInput(draft)) setDraft(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const commit = () => {
    const parsed = parseTimeInput(draft)
    if (parsed) {
      setDraft(parsed)
      onChange(parsed)
    } else {
      onChange('') // necháme draft viditelný, ale rodiči řekneme "nemám čas"
    }
  }

  const bad = draft.trim() !== '' && parseTimeInput(draft) === null

  return (
    <Input
      {...props}
      value={draft}
      inputMode="numeric"
      placeholder={props.placeholder ?? '8:30'}
      className={`font-mono tabular-nums ${bad ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''} ${className}`}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault() // ať Enter nejdřív srovná zápis a neodešle formulář
          commit()
        }
      }}
    />
  )
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Kliknutí mimo dialog schválně nezavírá — v rozepsaném formuláři je snadné
  // ťuknout vedle a přijít o všechno. Zavírá se Escapem, křížkem nebo Zrušit.
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-10 sm:pt-20">
      <div className="w-full max-w-lg rounded-xl bg-white p-4 shadow-xl sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold">{title}</h2>
          <button type="button" className="text-slate-400 hover:text-slate-600" onClick={onClose} aria-label="Zavřít">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

export function Card({ children, className = '', ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} {...props}>
      {children}
    </div>
  )
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  )
}
